import React, { useEffect, useState } from 'react'
import { Col, Form, Row, Skeleton } from 'antd'
import styled from 'styled-components'
import { useAppDispatch, useAppSelector } from 'src/store'
import { useLocation, useSearchParams } from 'react-router-dom'

import ButtonCustom from 'src/components/form/Button'
import { getMenus, getMenusByLp } from 'src/useCase/question'

import {
  FormValues,
  useMedicalSpecialtyForm,
} from 'src/containers/MedicalSpecialty/components/useMedicalSpecialtyForm'
import SelectField from 'src/components/form/SelectField'
import LocalStorage from 'src/utils/LocalStorage'
import choicesUserAction from 'src/store/domain/choicesUser/details'
import maleIcon from 'src/assets/icons/male.svg'
import femaleIcon from 'src/assets/icons/female.svg'
import { USER_GENDER } from 'src/constants/user'

const MedicalSpecialtyForm = () => {
  const location = useLocation()
  const dispatch = useAppDispatch()
  const [loadingSubmit, setLoadingSubmit] = useState(false)

  const { form, onSubmit } = useMedicalSpecialtyForm()
  const { control, handleSubmit, formState, setValue } = form
  const [searchParams] = useSearchParams()
  const clinicId = searchParams.get('clinicId')
  const accessToken = LocalStorage.accessToken
  const exists = searchParams.get('exists')
  const [selectGender, setSelectGender] = useState<string>()

  const menus = useAppSelector(
    (state) => state.domain.automaticPlan.menus.entity,
  )
  const choicesOfUser = useAppSelector(
    (state) => state.domain.choicesUser.choicesUser,
  )

  const loadingMenu =
    useAppSelector((state) => state.domain.automaticPlan.menus.loading) || false
  const menusOptions =
    Object.assign(menus).length > 0 &&
    Object.assign(menus).reduce((result, currentValue) => {
      result = [
        ...result,
        {
          id: currentValue.id,
          value: currentValue.id,
          label: currentValue.name,
        },
      ]
      return result
    }, [])

  const { menuId } = location.state || {}

  const setGender = (gender: number) => {
    setSelectGender(String(gender))
    LocalStorage.setLocalStorage('selected_gender', String(gender))
    setValue('menu_id', '')
  }

  const isMenuDisabled = Boolean(!selectGender && !accessToken)

  useEffect(() => {
    const isPaying = !!LocalStorage.getLocalStorage('is_paying')

    if (isPaying) {
      LocalStorage.removeLocalStorage('is_paying')
    }

    if (menuId) {
      setValue('menu_id', menuId)
    }
  }, [menuId, setValue])

  useEffect(() => {
    if (!selectGender || accessToken) return

    if (choicesOfUser?.from === 'LP' && !accessToken) {
      if (LocalStorage.getUserTreatmentId()) {
        LocalStorage.removeUserTreatmentId()
      }
      if (choicesOfUser?.menuId && choicesOfUser?.planId && !exists) {
        setValue('menu_id', choicesOfUser.menuId)
        setValue('plan_id', choicesOfUser.planId)

        setLoadingSubmit(true)
        handleSubmit(onSubmit)().finally(() => {
          setLoadingSubmit(false)
          setValue('menu_id', '')
          setValue('plan_id', '')
          dispatch(
            choicesUserAction.setChoicesData({
              clinicId: '',
              menuId: '',
              planId: '',
              from: '',
            }),
          )
        })
        return
      }

      if (clinicId) {
        dispatch(
          getMenusByLp({
            clinicId,
            params: { target_gender: Number(selectGender) },
          }),
        )
      }
    }
  }, [selectGender])

  useEffect(() => {
    if (accessToken) {
      const params = { allow_menu_ids: menuId ? [menuId] : [] }
      dispatch(getMenus(params))
    }
  }, [accessToken, menuId, dispatch])

  return (
    <Wrapper>
      {!accessToken && (
        <Row>
          <Col xs={24}>
            <Row className="justify-content-between">
              <p>性別（どちらかを選択してください）</p>
              <p>
                <span className="request">必須</span>
              </p>
            </Row>
            <Row className="justify-content-betwee mt-15 mb-30">
              <Col xs={11}>
                <GenderItem
                  onClick={() => setGender(USER_GENDER.MALE)}
                  className={
                    selectGender === `${USER_GENDER.MALE}` ? `selected` : ''
                  }
                >
                  <GenderImg src={maleIcon} alt="" />
                  <GenderHint>男性</GenderHint>
                </GenderItem>
              </Col>
              <Col xs={11} className="ml-20">
                <GenderItem
                  onClick={() => setGender(USER_GENDER.FEMALE)}
                  className={
                    selectGender === `${USER_GENDER.FEMALE}` ? `selected` : ''
                  }
                >
                  <GenderImg src={femaleIcon} alt="" />
                  <GenderHint>女性</GenderHint>
                </GenderItem>
              </Col>
            </Row>
          </Col>
        </Row>
      )}

      {loadingMenu || loadingSubmit ? (
        <Skeleton />
      ) : (
        <Form onFinish={handleSubmit(onSubmit)}>
          <SelectField<FormValues>
            label="診療科目"
            selectProps={{
              options: menusOptions,
              loading: false,
              allowClear: false,
              placeholder: '選択してください',
              disabled: isMenuDisabled,
            }}
            controller={{ name: 'menu_id', control }}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          />
          <ButtonCustom
            className="btn-default btn-chestnut-rose br-60 mt-30"
            loading={formState.isSubmitting}
            htmlType="submit"
            name="問診へ進む"
            showIconRight
          />
        </Form>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  .ant-select:not(.ant-select-customize-input) .ant-select-selector {
    background-color: var(--color-pampas);
    border: 1px solid var(--color-pampas);
    border-radius: 10px;
  }

  .ant-select-selection-placeholder {
    display: flex;
    align-items: center;
  }

  .request {
    border: 1px solid var(--color-mandys-pink);
    color: var(--color-white);
    background-color: var(--color-mandys-pink);
    padding: 3px 7px 0;
    font-size: 11px;
    font-weight: 700;
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;
    -webkit-align-items: center;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
  }
`
const GenderItem = styled.div`
  display: flex;
  border: none;
  padding: 10px;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid var(--color-pampas);

  &.selected {
    border-color: #980;
    background-color: #f1efe7;
    border: unset;
  }
`
const GenderHint = styled.p`
  height: 40px;
  display: flex;
  align-items: center;
  font-weight: 700;
  font-size: 13px;
  margin-left: 10px;
`
const GenderImg = styled.img`
  width: 40px;
  height: 40px;
`

export default MedicalSpecialtyForm
